import moment from 'moment';
import { ChartExportRecord, formatGmtOffsetLabel, recordUtcOffsetMinutes, recordWallClock } from './chart-export.models';
import {
  MAIA_DESKTOP_MMI_NAMESPACE,
  MILLISECONDS_BETWEEN_YEAR_1_AND_UNIX_EPOCH,
  TICKS_PER_MILLISECOND,
} from '../import/maia-desktop-mmi.parser';

export type MaiaDesktopMmiVariant = 'v2' | 'v3';

/**
 * Lege RTF-notitie zoals Maia Mechanics Desktop v2 hem per chart schrijft. De import herkent een
 * v2-bestand aan dit element (v3 schrijft het niet meer), dus de v2-variant zet het altijd.
 */
const V2_EMPTY_COMMENTS =
  '{\\rtf1\\ansi\\ansicpg1252\\deff0\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Tahoma;}}\r\n\\viewkind4\\uc1\\pard\\f0\\fs17\\par\r\n}\r\n';

/**
 * Schrijft charts als Maia Mechanics Desktop-document (.mmi), nagebootst op de echte v2- en
 * v3-exports (docs/human-design/hd-chart-export.md §3; leeskant: hd-chart-import.md §3):
 *  - `BirthDateLocal`/`BirthDateUT` als .NET-ticks (100 ns sinds 0001-01-01); het UT-moment is
 *    exact, de lokale ticks dragen de kloktijd. Zonder echte lokale tijd zijn beide gelijk.
 *  - `TimeZone` is in Maia enkel weergavetekst. v3: de IANA-id (zo schrijft v3 nieuwe charts);
 *    v2: de oude vorm "AFKORTING (UT - 1:00)" met Maia's omgekeerde teken (UT − offset). Zonder
 *    echte lokale tijd: "UT".
 *  - `BirthLocation` "Stad, Land" (Engelse landnaam); geen stad → geen TimeZone en geen
 *    BirthLocation, zoals Maia charts zonder locatie schrijft.
 *  - Alleen `RaveChart`; `OtherDateUT` (Maia's transitvenster) = het exportmoment.
 * XML-declaratie utf-8, CRLF-regeleinden, geen BOM.
 */
export function buildMaiaDesktopMmi(
  records: readonly ChartExportRecord[],
  variant: MaiaDesktopMmiVariant,
  exportedAt: moment.Moment,
): string {
  const otherDateTicks = toDotNetTicks(exportedAt);
  const lines = ['<?xml version="1.0" encoding="utf-8"?>', `<Charts xmlns="${MAIA_DESKTOP_MMI_NAMESPACE}">`];

  records.forEach((record, index) => {
    lines.push('  <Chart>');
    lines.push(element('ID', String(index + 1)));
    lines.push(element('DisplayName', record.name));
    lines.push(element('ChartType', 'RaveChart'));
    lines.push(element('BirthDateLocal', toDotNetTicks(recordWallClock(record))));
    lines.push(element('BirthDateUT', toDotNetTicks(record.birthUtc)));
    lines.push(element('OtherDateUT', otherDateTicks));
    const location = birthLocationText(record);
    if (location) {
      lines.push(element('TimeZone', timeZoneText(record, variant)));
      lines.push(element('BirthLocation', location));
    }
    if (variant === 'v2') {
      lines.push(element('Comments', V2_EMPTY_COMMENTS));
      lines.push('    <DocumentAttributes>');
      lines.push('      <DocumentAttribute AttKey="JobPosition" AttValue="" />');
      lines.push('      <DocumentAttribute AttKey="IsLocalTimeInGmt" AttValue="False" />');
      lines.push('    </DocumentAttributes>');
    }
    lines.push('  </Chart>');
  });

  lines.push('</Charts>');
  return lines.join('\r\n') + '\r\n';
}

/** UTC-moment (of wandklok als UTC-moment) → .NET-ticks; BigInt want ticks passen niet in een Number. */
export function toDotNetTicks(utcMoment: moment.Moment): string {
  return ((BigInt(utcMoment.valueOf()) + MILLISECONDS_BETWEEN_YEAR_1_AND_UNIX_EPOCH) * TICKS_PER_MILLISECOND).toString();
}

function timeZoneText(record: ChartExportRecord, variant: MaiaDesktopMmiVariant): string {
  if (!record.birthLocal || !record.timezone) return 'UT';
  if (variant === 'v3') return record.timezone;
  const offsetMinutes = recordUtcOffsetMinutes(record);
  // Maia's teken is omgekeerd: UT = lokaal − offset, dus CET (+1) wordt "UT - 1:00".
  const sign = offsetMinutes > 0 ? '-' : '+';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hoursMinutes = `${Math.floor(absoluteMinutes / 60)}:${String(absoluteMinutes % 60).padStart(2, '0')}`;
  return `${timeZoneAbbreviation(record.timezone, record.birthUtc, offsetMinutes)} (UT ${sign} ${hoursMinutes})`;
}

/**
 * Afkorting zoals Maia ze toonde ("CET", "EST", "BST"): de korte naam uit de tz-database van de
 * browser, eerst Amerikaans-Engels (VS-zones) en dan Brits-Engels (Europese zones). Geeft de
 * browser enkel "GMT+1", dan dat label; het is in Maia toch puur weergave.
 */
function timeZoneAbbreviation(timezone: string, birthUtc: moment.Moment, offsetMinutes: number): string {
  for (const locale of ['en-US', 'en-GB']) {
    try {
      const shortName = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: 'short' })
        .formatToParts(birthUtc.valueOf())
        .find(part => part.type === 'timeZoneName')?.value;
      if (shortName && /^[A-Z]{2,5}$/.test(shortName) && (shortName !== 'GMT' || offsetMinutes === 0)) return shortName;
    } catch {
      break;
    }
  }
  return formatGmtOffsetLabel(offsetMinutes);
}

function birthLocationText(record: ChartExportRecord): string | undefined {
  if (!record.city) return undefined;
  return [record.city, record.countryName ?? record.countryCode].filter(Boolean).join(', ');
}

function element(tagName: string, value: string): string {
  return `    <${tagName}>${escapeXml(value)}</${tagName}>`;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
